// Biometric helpers for storing and retrieving the refresh token securely
// using @capgo/capacitor-native-biometric

import { Capacitor } from "@capacitor/core";
import { NativeBiometric } from "@capgo/capacitor-native-biometric";
import * as Sentry from "@sentry/react";
import {
  BIOMETRIC_CREDENTIAL_SAVED_KEY,
  BiometricEnrollmentError,
} from "../constants";
import { getPreference, removePreference, setPreference } from "./preferences";
import { ENABLE_KEYCHAIN_AUDIT_LOGS } from "../constants/logging";

const KEYCHAIN_AUDIT_TAG = "[KEYCHAIN-AUDIT]";

const auditLog = (message: string, data?: Record<string, unknown>) => {
  if (!ENABLE_KEYCHAIN_AUDIT_LOGS) return;
  // eslint-disable-next-line no-console
  console.log(`${KEYCHAIN_AUDIT_TAG} ${message}`, data ?? {});
};

type BiometricSessionPayload = {
  refreshToken: string;
  serverDiffMs?: number | null;
  refreshExpiresAt?: string | null;
  state?: string | null;
};

type BiometricStoredCredential = {
  username?: string;
  refreshToken: string;
  serverDiffMs: number | null;
  refreshExpiresAt: string | null;
  state?: string | null;
};

export const BIOMETRIC_SERVER = "allaria-auth"; // logical namespace for credentials

export async function isBiometricAvailable() {
  if (!Capacitor.isNativePlatform()) return { isAvailable: false } as const;

  try {
    const res = await NativeBiometric.isAvailable();
    return res;
  } catch {
    return { isAvailable: false } as const;
  }
}

// Error thrown by promptBiometric that carries both the mapped enum and the
// raw native code/message so callers can log the native details in telemetry.
export class BiometricPromptError extends Error {
  constructor(
    public readonly type: BiometricEnrollmentError,
    public readonly code?: string,
    public readonly nativeMessage?: string,
  ) {
    super(type);
    this.name = "BiometricPromptError";
  }
}

export const promptBiometric = async () => {
  if (!Capacitor.isNativePlatform()) return false;

  try {
    await NativeBiometric.verifyIdentity({
      description: "Autenticación requerida para ingresar",
      fallbackTitle: "Usar contraseña", // Only IOS
      maxAttempts: 3, // Only Android
      negativeButtonText: "Cancelar",
      reason: "Para continuar, necesitamos verificar tu identidad", // Only IOS
      title: "Verificación biométrica", // Only Android
      useFallback: false,
    });

    return true;
  } catch (error) {
    const typed = error as { code?: string; message?: string };
    const errorType = handleBiometricVerificationError(typed);

    throw new BiometricPromptError(errorType, typed.code, typed.message);
  }
};

export const handleBiometricVerificationError = (error: { code?: string }) => {
  let errorMessage: BiometricEnrollmentError = BiometricEnrollmentError.UNKNOWN_ERROR;
  if (error.code) {
    switch (error.code) {
      case "1": // Biometrics Unavailable
      case "3": // Biometrics Not Enrolled
        errorMessage = BiometricEnrollmentError.BIOMETRY_NOT_AVAILABLE;
        break;
      case "2": // User Lockout
        errorMessage = BiometricEnrollmentError.USER_LOCKOUT;
        break;
      case "4": // Temporary Lockout (Android)
        errorMessage = BiometricEnrollmentError.USER_TEMPORARY_LOCKOUT;
        break;
      case "10": // Authentication Failed (real biometric mismatch)
        errorMessage = BiometricEnrollmentError.BIOMETRIC_VERIFICATION_FAILED;
        break;
      case "11": // App Cancel (iOS — the app itself invalidated the LAContext)
      case "13": // Not Interactive (iOS — non-interactive LAContext cannot show prompt)
      case "15": // System Cancel (Android / iOS — OS interrupted the prompt, e.g. screen lock or backgrounded)
        errorMessage = BiometricEnrollmentError.SYSTEM_CANCELLED;
        break;
      case "14": // Passcode Not Set
        errorMessage = BiometricEnrollmentError.PASSCODE_NOT_SET;
        break;
      case "16": // User Cancel
      case "17": // User Fallback
        errorMessage = BiometricEnrollmentError.USER_CANCELLED;
        break;
      case "0": // Unknown Error
      case "12": // Invalid Context (iOS)
      default:
        errorMessage = BiometricEnrollmentError.UNKNOWN_ERROR;
        break;
    }
  }

  return errorMessage;
};

export async function saveRefreshTokenSecure(
  username: string,
  session: BiometricSessionPayload
) {
  if (!Capacitor.isNativePlatform()) return;

  const payload = JSON.stringify({
    v: 1,
    refreshToken: session.refreshToken,
    serverDiffMs: session.serverDiffMs ?? null,
    refreshExpiresAt: session.refreshExpiresAt ?? null,
    state: session.state ?? null,
  });

  // Set the saved-marker BEFORE writing to the keychain. If a concurrent
  // read sneaks in between the two operations, the new ordering means the
  // worst case is "flag present, credentials absent" → returns undefined
  // safely. The reverse ordering would let getRefreshTokenSecure observe
  // "flag absent, credentials present", interpret it as a reinstall, and
  // wipe the credentials we just stored.
  try {
    await setPreference(BIOMETRIC_CREDENTIAL_SAVED_KEY, "true");
  } catch {
    // ignore preference failures; orphan creds will be wiped on next read
  }

  const result = await NativeBiometric.setCredentials({
    username,
    password: payload,
    server: BIOMETRIC_SERVER,
  });

  return result;
}

export async function getRefreshTokenSecure(
  source = "unknown",
): Promise<BiometricStoredCredential | undefined> {
  if (!Capacitor.isNativePlatform()) return undefined;

  // iOS Keychain persists across app uninstall, but Capacitor Preferences
  // (NSUserDefaults) does not. A missing flag with credentials in the
  // keychain means we're on a fresh install and the credentials are
  // orphans from a previous installation — wipe them.
  // Only treat it as a reinstall if the keystore actually has an orphaned
  // entry. Users who never enrolled biometrics also have flag !== "true",
  // and we must not fire the event or attempt cleanup on every session.
  const flag = await getPreference(BIOMETRIC_CREDENTIAL_SAVED_KEY);
  if (flag !== "true") {
    let hasOrphanedCredential = false;
    try {
      const orphan = await NativeBiometric.getCredentials({
        server: BIOMETRIC_SERVER,
      });
      hasOrphanedCredential = Boolean(orphan?.password);
    } catch {
      // Nothing in the store — clean state, no action needed.
    }
    if (hasOrphanedCredential) {
      auditLog("reinstall detected, clearing keychain residue", { source });
      try {
        Sentry.captureMessage("biometric.keychain_reinstall_detected", {
          level: "info",
          tags: { module: "biometric", source },
        });
      } catch {
        // Sentry must not block.
      }
      await deleteRefreshTokenSecure();
    }
    return undefined;
  }

  try {
    const creds = await NativeBiometric.getCredentials({
      server: BIOMETRIC_SERVER,
    });

    if (!creds?.password) {
      auditLog("no credentials", { source });
      return undefined;
    }

    try {
      const parsed = JSON.parse(creds.password);

      if (parsed?.refreshToken) {
        auditLog("credentials found", {
          source,
          hasUsername: Boolean(creds.username),
        });
        return {
          username: creds.username || undefined,
          refreshToken: parsed.refreshToken as string,
          serverDiffMs:
            typeof parsed.serverDiffMs === "number"
              ? parsed.serverDiffMs
              : null,
          refreshExpiresAt: (parsed.refreshExpiresAt as string) ?? null,
          state:
            typeof parsed.state === "string" ? (parsed.state as string) : null,
        };
      }
    } catch {
      // ignore
    }

    auditLog("parse failed, deleting entry", { source });
    await deleteRefreshTokenSecure();
    return undefined;
  } catch (error) {
    auditLog("native call threw", {
      source,
      error: error instanceof Error ? error.message : String(error),
    });
    return undefined;
  }
}

export async function deleteRefreshTokenSecure() {
  if (Capacitor.isNativePlatform()) {
    try {
      await NativeBiometric.deleteCredentials({ server: BIOMETRIC_SERVER });
    } catch {
      // ignore
    }
  }

  try {
    await removePreference(BIOMETRIC_CREDENTIAL_SAVED_KEY);
  } catch {
    // ignore preference removal errors
  }
}
